import { useEffect, useState } from "react";
import { totpNow, TOTP_STEP } from "../crypto";
import { errMsg } from "../lib/api";
import { expiryInfo, fmtBytes, fmtDay } from "../lib/format";
import { MONO_KEYS, TYPES } from "../lib/itemTypes";
import type { Field, Item, Vault } from "../lib/types";
import { OWNER_ORG, useVault } from "../state/store";
import { Badge, Button, IconButton, IconTile, Input, Switch } from "../ui/ds";
import { useIsMobile, useNow, useWidth } from "../ui/hooks";
import { Icon } from "../ui/Icon";
import { usePerspective, useUI } from "./ui";

/** Items tab: category tiles home → list + detail ("Sheet" layout). */
export function ItemsTab({ vault }: { vault: Vault }) {
  const p = usePerspective();
  const { nav, go, setModal } = useUI();
  const mobile = useIsMobile();

  const visible = p.visibleItems(vault);
  const live = visible.filter((i) => !i.archivedAt);
  const arch = p.asClient ? [] : visible.filter((i) => i.archivedAt);
  const cats = p.cats(vault);
  const uncat = live.filter((i) => !cats.some((c) => c.id === i.catId));
  const canAddCat = !p.asClient;

  const catSel =
    nav.cat === null
      ? null
      : nav.cat === "all" || nav.cat === "__none" || (nav.cat === "__arch" && arch.length) || cats.some((c) => c.id === nav.cat)
        ? nav.cat
        : "all";

  if (catSel === null) {
    const preview = (arr: Item[]) => (arr.length ? arr.slice(0, 3).map((i) => i.title).join(" · ") : "Empty");
    const tiles = [
      { id: "all", name: "All items", icon: "layers", arr: live },
      ...cats.map((c) => ({ id: c.id, name: c.name, icon: c.icon, arr: live.filter((i) => i.catId === c.id) })),
      ...(uncat.length && cats.length ? [{ id: "__none", name: "Uncategorised", icon: "inbox", arr: uncat }] : []),
      ...(arch.length ? [{ id: "__arch", name: "Archived", icon: "archive", arr: arch }] : []),
    ];
    return (
      <div className="scroll page-body">
        <div className="tile-grid">
          {tiles.map((t) => (
            <button key={t.id} type="button" className="cat-tile hover-lift" onClick={() => go({ cat: t.id, itemId: null, pane: "list", q: "" })}>
              <div className="accent-rule" />
              <div className="cat-tile-top">
                <IconTile icon={t.icon} size={40} />
                <span className="cat-tile-count">
                  {t.arr.length} {t.arr.length === 1 ? "item" : "items"}
                </span>
              </div>
              <div style={{ marginTop: "auto", minWidth: 0 }}>
                <div className="cat-tile-name">{t.name}</div>
                <div className="cat-tile-prev">{preview(t.arr)}</div>
              </div>
            </button>
          ))}
          {canAddCat ? (
            <button type="button" className="cat-tile-new" onClick={() => setModal({ kind: "cat" })}>
              <Icon name="plus" size={20} />
              <span>New category</span>
            </button>
          ) : null}
        </div>
        {!p.canSee(vault) ? (
          <p className="muted" style={{ marginTop: 16, fontSize: 13 }}>
            You can add credentials to this vault but not see existing ones. Anything you add shows here.
          </p>
        ) : null}
      </div>
    );
  }

  const q = nav.q.trim().toLowerCase();
  const inCat =
    catSel === "__arch" ? arch : live.filter((i) => catSel === "all" || (catSel === "__none" ? !cats.some((c) => c.id === i.catId) : i.catId === catSel));
  const list = inCat
    .filter((i) => !q || `${i.title} ${i.sub}`.toLowerCase().includes(q))
    .sort((a, b) => a.title.localeCompare(b.title, "en-GB", { sensitivity: "base" }));
  let item = list.find((i) => i.id === nav.itemId) ?? (nav.itemId ? visible.find((i) => i.id === nav.itemId) : undefined);
  if (!item && !mobile) item = list[0];
  const crumb =
    catSel === "all" ? "All items" : catSel === "__none" ? "Uncategorised" : catSel === "__arch" ? "Archived" : (cats.find((c) => c.id === catSel)?.name ?? "");
  const realCat = cats.find((c) => c.id === catSel);
  const showList = !mobile || nav.pane === "list";
  const showDetail = !mobile || nav.pane === "detail";

  return (
    <div className="items-row">
      {showList ? (
        <div className="item-list">
          <div className="item-list-head">
            <div className="crumb">
              <button type="button" className="linklike" onClick={() => go({ cat: null, itemId: null, pane: "list", q: "" })}>
                ← Categories
              </button>
              <span style={{ color: "var(--text-subtle)" }}>/</span>
              <span className="crumb-name">{crumb}</span>
              {realCat && canAddCat ? (
                <IconButton icon="trash-2" label="Delete category" variant="ghost" size="sm" onClick={() => setModal({ kind: "delcat", catId: realCat.id })} />
              ) : null}
            </div>
            <Input icon="search" size="sm" placeholder="Search this vault" value={nav.q} aria-label="Search this vault" onChange={(v) => go({ q: v })} />
          </div>
          <div className="item-list-body">
            {list.map((i) => {
              const ex = i.archivedAt ? null : expiryInfo(i);
              return (
                <button
                  key={i.id}
                  type="button"
                  className={`item-row${item && i.id === item.id && !mobile ? " is-on" : ""}`}
                  onClick={() => go({ itemId: i.id, pane: "detail" })}
                >
                  <IconTile icon={TYPES[i.type]?.icon ?? "file"} />
                  <div className="item-row-text">
                    <div className="item-row-title">{i.title}</div>
                    <div className="item-row-sub">{i.sub}</div>
                  </div>
                  {vault.kind === "client" && !p.asClient && i.private ? (
                    <span title={`Not shared with ${vault.name}`} style={{ display: "flex" }}>
                      <Icon name="eye-off" size={14} style={{ color: "var(--text-muted)" }} title={`Not shared with ${vault.name}`} />
                    </span>
                  ) : null}
                  {ex && ex.status !== "ok" ? (
                    <Badge tone={ex.tone} dot>
                      {ex.short}
                    </Badge>
                  ) : null}
                  {i.totp && i.type !== "totp" ? <Icon name="timer" size={14} style={{ color: "var(--text-subtle)" }} title="Has a 2FA code" /> : null}
                </button>
              );
            })}
            {!list.length ? (
              <div className="list-empty">
                {!p.canSee(vault)
                  ? "You can add credentials to this vault but not see existing ones. Anything you add shows here."
                  : q
                    ? `Nothing matches “${nav.q}”.`
                    : "Nothing here yet."}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
      {showDetail ? (
        <div className="detail-pane">
          {item ? <ItemDetail key={item.id} item={item} vault={vault} /> : <div className="detail-empty">Pick an item to see it here.</div>}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail
// ---------------------------------------------------------------------------

function maskValue(f: Field): string {
  if (!f.m) return "•".repeat(12);
  // .env style: keep the keys visible, mask the values.
  return f.v
    .split("\n")
    .map((l) => (l.includes("=") ? `${l.split("=")[0]}=••••••••` : "••••••••••"))
    .join("\n");
}

function useTotp(secret: string | undefined) {
  const now = useNow();
  const [code, setCode] = useState<string | null>(null);
  const step = Math.floor(now / 1000 / TOTP_STEP);
  useEffect(() => {
    if (!secret) return;
    let live = true;
    void totpNow(secret).then((c) => live && setCode(c));
    return () => {
      live = false;
    };
  }, [secret, step]);
  const left = TOTP_STEP - (Math.floor(now / 1000) % TOTP_STEP);
  return { code: secret ? code : null, left };
}

function ItemDetail({ item, vault }: { item: Item; vault: Vault }) {
  const v = useVault();
  const p = usePerspective();
  const { go, setModal } = useUI();
  const mobile = useIsMobile();
  const width = useWidth();
  const [revealed, setRevealed] = useState<Record<number, boolean>>({});
  const totpSecret = item.type === "totp" ? item.fields.find((f) => f.k === "Setup key")?.v : item.totp;
  const totp = useTotp(totpSecret);

  const paneW = mobile ? width : width - 248 - (width < 1180 ? 320 : 380);
  const stacked = paneW < 560;
  const canEdit = p.canEdit(vault);
  const isOwnerView = !p.asClient;
  const cat = p.cats(vault).find((c) => c.id === item.catId);
  const ex = expiryInfo(item);
  const archived = !!item.archivedAt;
  const fail = (what: string) => (e: unknown) => v.toast(what, errMsg(e), "danger");

  const reveal = (i: number, f: Field) => {
    const on = !revealed[i];
    setRevealed((r) => ({ ...r, [i]: on }));
    if (on) void v.logAccess(item.vaultId, `revealed ${f.k.toLowerCase()} on ${item.title}`, item.id, "eye");
  };
  const copyField = (f: Field) => {
    void v.copy(f.v, f.k);
    void v.logAccess(item.vaultId, `copied ${f.k.toLowerCase()} from ${item.title}`, item.id, "copy");
  };

  const accessText =
    vault.kind === "personal"
      ? "Only you"
      : p.asClient
        ? `Shared by ${OWNER_ORG}`
        : item.private
          ? "Only you"
          : `Shared with ${vault.name}`;

  return (
    <div className={`detail${stacked ? " is-stacked" : ""}`}>
      {mobile ? (
        <button type="button" className="back-link" style={{ minHeight: 36 }} onClick={() => go({ pane: "list" })}>
          ← {vault.name}
        </button>
      ) : null}
      <div className="detail-head">
        <div style={{ flex: "1 1 200px", minWidth: 0 }}>
          <div className="detail-title">{item.title}</div>
          <div className="detail-type">
            <Icon name={TYPES[item.type]?.icon ?? "file"} size={14} style={{ color: "var(--deep-teal)" }} />
            <span>{TYPES[item.type]?.label}</span>
            {cat ? <span>· {cat.name}</span> : null}
          </div>
        </div>
        <div className="detail-actions">
          {canEdit && !archived ? <IconButton icon="pencil" label="Edit" variant="ghost" size="sm" onClick={() => setModal({ kind: "item", itemId: item.id })} /> : null}
          {isOwnerView ? (
            <>
              <IconButton icon="link" label="Share by link" variant="ghost" size="sm" onClick={() => setModal({ kind: "share", tab: "link", itemId: item.id })} />
              <IconButton
                icon={archived ? "archive-restore" : "archive"}
                label={archived ? "Unarchive" : "Archive"}
                variant="ghost"
                size="sm"
                onClick={() => {
                  void v.setItemArchived(item, !archived).then(() => !archived && go({ itemId: null }), fail("Couldn't change that"));
                }}
              />
              <IconButton icon="trash-2" label="Delete" variant="ghost" size="sm" onClick={() => setModal({ kind: "confirm-delete", itemId: item.id })} />
            </>
          ) : null}
        </div>
      </div>

      {archived ? (
        <div className="banner banner-archived">
          <Icon name="archive" size={18} style={{ color: "var(--text-muted)" }} />
          <div className="banner-text">
            <strong>Archived {fmtDay(item.archivedAt!)}.</strong> Hidden from lists, search and expiry warnings. Clients can't see it.
          </div>
          {isOwnerView ? (
            <Button variant="secondary" size="sm" icon="archive-restore" onClick={() => void v.setItemArchived(item, false).then(() => go({ cat: "all" }), fail("Couldn't restore it"))}>
              Unarchive
            </Button>
          ) : null}
        </div>
      ) : ex && ex.status !== "ok" ? (
        <div className={`banner ${ex.status === "expired" ? "banner-danger" : "banner-warning"}`}>
          <Icon name="calendar-clock" size={18} />
          <div className="banner-text">
            <strong>
              {ex.status === "expired"
                ? `Expired on ${ex.date}.`
                : ex.days === 0
                  ? "Expires today."
                  : `Expires in ${ex.days} ${ex.days === 1 ? "day" : "days"}, on ${ex.date}.`}
            </strong>{" "}
            {ex.status === "expired"
              ? "Anything using it has probably stopped working. Rotate it, then update this item."
              : "Rotate it before then and update this item with the new value."}
          </div>
          {canEdit ? (
            <Button variant="secondary" size="sm" icon="refresh-cw" onClick={() => setModal({ kind: "item", itemId: item.id })}>
              Update item
            </Button>
          ) : null}
        </div>
      ) : null}

      <div className="sheet">
        {item.fields.map((f, i) => {
          const mono = f.s || f.m || MONO_KEYS.has(f.k);
          return (
            <div className="sheet-row" key={i}>
              <div className="sheet-label">{f.k}</div>
              <div className={`sheet-value${mono ? " is-mono" : ""}${f.m ? " is-multi" : ""}`}>
                {f.s && !revealed[i] ? maskValue(f) : f.v || <span className="muted">—</span>}
              </div>
              <div className="sheet-actions">
                {f.s ? <IconButton icon={revealed[i] ? "eye-off" : "eye"} label={revealed[i] ? "Hide" : "Reveal"} variant="ghost" size="sm" onClick={() => reveal(i, f)} /> : null}
                {f.v ? <IconButton icon="copy" label={`Copy ${f.k.toLowerCase()}`} variant="ghost" size="sm" onClick={() => copyField(f)} /> : null}
              </div>
            </div>
          );
        })}
        {totpSecret ? (
          <div className="sheet-row">
            <div className="sheet-label">2FA code</div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
              {totp.code ? (
                <>
                  <span className="totp-code">{`${totp.code.slice(0, 3)} ${totp.code.slice(3)}`}</span>
                  <div className="totp-bar" aria-hidden>
                    <div style={{ width: `${(totp.left / TOTP_STEP) * 100}%` }} />
                  </div>
                  <span className="mono" style={{ fontSize: 12, color: "var(--text-muted)" }}>
                    {totp.left}s
                  </span>
                </>
              ) : (
                <span className="muted" style={{ fontSize: 13 }}>
                  The setup key isn’t valid base32.
                </span>
              )}
            </div>
            <div className="sheet-actions">
              {totp.code ? (
                <IconButton
                  icon="copy"
                  label="Copy code"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    void v.copy(totp.code!, "2FA code");
                    void v.logAccess(item.vaultId, `copied the 2FA code from ${item.title}`, item.id, "timer");
                  }}
                />
              ) : null}
            </div>
          </div>
        ) : null}
        {ex ? (
          <div className="sheet-row">
            <div className="sheet-label">Expires</div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", minWidth: 0 }}>
              <span className="mono" style={{ fontSize: 14, color: "var(--ink-navy)" }}>
                {ex.date}
              </span>
              <Badge tone={ex.tone} dot>
                {ex.text}
              </Badge>
            </div>
            <div style={{ fontSize: 12, color: "var(--text-muted)", textAlign: "right", paddingRight: 8 }}>Warns {item.remind || 30} days before</div>
          </div>
        ) : null}
        {vault.kind === "client" && isOwnerView ? (
          <div className="sheet-row is-switch">
            <div className="sheet-label">Client access</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, fontSize: 14, color: "var(--ink-navy)" }}>
              <Icon name={item.private ? "eye-off" : "users"} size={16} style={{ color: "var(--deep-teal)" }} />
              <span>{item.private ? "Only you" : `Shared with ${vault.name}`}</span>
            </div>
            <Switch
              checked={!item.private}
              disabled={archived}
              ariaLabel={`Share with ${vault.name}`}
              onChange={(on) => void v.setItemPrivate(item, !on).catch(fail("Couldn't change sharing"))}
            />
          </div>
        ) : null}
        {item.file ? (
          <div className="sheet-row">
            <div className="sheet-label">Attachment</div>
            <div className="mono" style={{ fontSize: 13, color: "var(--ink-navy)", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
              {item.file.name} · {fmtBytes(item.file.size)}
            </div>
            <div className="sheet-actions">
              <IconButton icon="download" label="Download" variant="ghost" size="sm" onClick={() => void v.downloadFile(item).catch(fail("Couldn't download it"))} />
            </div>
          </div>
        ) : null}
      </div>

      <div className="meta-grid">
        <div className="muted">Vault</div>
        <div>{vault.name}</div>
        <div className="muted">Updated</div>
        <div>{fmtDay(item.updatedAt)}</div>
        <div className="muted">Access</div>
        <div>{accessText}</div>
      </div>
    </div>
  );
}
